import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useUserSession } from "../../context/UserContext";
import { apiGet } from "../../api/client";
import { getApiToken } from "../../data/api/token";

interface DriverAvatarProps {
  driverId: string;
  workspaceId: string;
  name: string;
  size?: number;
}

/** Fixed dimensions: loading, missing photos and failures never resize a row. */
export function DriverAvatar({
  driverId,
  workspaceId,
  name,
  size = 20,
}: DriverAvatarProps) {
  const { userSession, isAuthenticated } = useUserSession();
  const holder = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [failedSource, setFailedSource] = useState<string>();
  useEffect(() => {
    if (!holder.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(holder.current);
    return () => observer.disconnect();
  }, []);
  const allowed = !!userSession && isAuthenticated();
  const photo = useQuery<string | null>(
    [
      "driver-photo",
      userSession?.user.userId || userSession?.user.id,
      workspaceId,
      driverId,
    ],
    async () => {
      const result = await apiGet(
        await getApiToken(),
        `/api/drivers/${encodeURIComponent(driverId)}/photo`,
        workspaceId,
      );
      const value = result?.data?.photo;
      return typeof value === "string" &&
        value.startsWith("data:image/jpeg;base64,")
        ? value
        : null;
    },
    {
      enabled: visible && allowed && !!workspaceId && !!driverId,
      staleTime: 60_000,
      cacheTime: 300_000,
      retry: false,
      refetchOnWindowFocus: true,
    },
  );
  const src =
    allowed && !photo.isError && photo.data !== failedSource
      ? photo.data
      : null;
  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <span
      ref={holder}
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200"
      style={{
        width: size,
        height: size,
        minWidth: size,
        maxHeight: size,
        fontSize: Math.max(9, size * 0.36),
        lineHeight: 1,
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          className="h-full w-full object-cover"
          onError={() => setFailedSource(src)}
        />
      ) : (
        initials
      )}
    </span>
  );
}
